
import {throwError as observableThrowError, of as observableOf, Observable, empty} from 'rxjs';

import {catchError, map, switchMap} from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';



import {ApiMessageService, MessageType} from '../../core/api-message.service';
import {SpringError} from '../model/spring-error.model';
import {Base} from '../model/base.model';
import {Page} from '../model/page.model';
import {Helpers} from '../helpers/helpers';
import {Router} from '@angular/router';
import {Enum} from '../model/enum.model';
import {ConfigService} from '../../core/config.service';
import {Attachment} from '../model/attachment.model';
import {ErrorObservable} from 'rxjs-compat/observable/ErrorObservable';
import {ActivatedRoute} from '@angular/router';

export abstract class CommonService<T extends Base> {

  // this.apiMessageService.onLoad.next(false);
  // this.apiMessageService.onLoad.next(true);

  static PAGE_OFFSET = 10;

  public constructor(protected httpClient: HttpClient,
                     protected apiMessageService: ApiMessageService,
                     protected router: Router,
                     protected configService: ConfigService) {}

  protected _buildInstance(json: any): T {
    if (json) {
      const instance: T = this.buildInstance(json);
      if (!instance.hasId()) {
        throw new TypeError('Errore interno all\'applicazione codice 1');
      }

      // se serve popolarli anche nel builder (ex. select2) vanno spostati li.
      instance.setPermissions(json.permissions);

      return instance;
    }
    return null;
  }

  protected generateError(responseError: HttpErrorResponse): Observable<T>{
    if (responseError.status === 204) {  // No Content
      return observableOf(null);
    }
    if (responseError.status === 404) {  // Not Found
      this.apiMessageService.sendMessage(MessageType.ERROR, 'Risorsa non trovata!');
      console.log('not found');
      return observableThrowError('not found');
    }
    const springError = new SpringError(responseError);
    this.apiMessageService.sendMessage(MessageType.ERROR, springError.getRestErrorMessage());
    return observableThrowError(responseError);
  }

  public getRequestMapping(): string {
    return this.getApiService() + this.getApiPath();
  }

  public getApiPath(): string {
    return '/v' + this.getApiVersion() + '/' + this.getRoute();
  }

  public getSelect2Mapping(): string {
    return this.getRequestMapping() + '/select2';
  }

  public abstract buildInstance(json: any): T;

  public abstract getModule(): string;

  public abstract getRoute(): string;

  public abstract getApiService(): string;

  public getApiVersion() {
    return 1;
  }

  public saveMessage(): string {
    return 'Elemento salvato correttamente.';
  }

  public deleteMessage(): string {
    return 'Elemento cancellato correttamente.';
  }

  /**
   * Get All.
   * @returns {Observable<T[]>}
   */
  public getAll(filter?: {}, path?: string): Observable<T[]> {
    path = path ? path : '';

    let params = new HttpParams();

    params = this.appendToImmutableHttpParams(filter, params);

    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {
          return this.httpClient.get<Page<T>>( apiBase + this.getRequestMapping() + path, {params: params})
            .pipe(
              map((result) => {
                try {
                  if (!result.items) {
                    return [];
                  }
                  const items: T[] = result.items.map((item) => {
                    const instance: T = this._buildInstance(item);
                    return instance;
                  });
                  return items;
                } catch (ex) {
                  console.log(ex);
                  this.apiMessageService.sendMessage(MessageType.ERROR, ex.message);
                  observableThrowError(ex);
                }
              }),
              catchError( (httpErrorResponse: HttpErrorResponse) => {
                const springError = new SpringError(httpErrorResponse);
                this.apiMessageService.sendMessage(MessageType.ERROR,  springError.getRestErrorMessage());
                return observableThrowError(springError);
              })
            );
        })
      );
  }

  /**
   * Get Pageable.
   * @param {number} page
   * @returns {Observable<Page<T extends Base>>}
   */
  public getPageable(page: number, filter: {}): Observable<Page<T>> {

    let params = new HttpParams().set('page', page + '').set('offset', CommonService.PAGE_OFFSET + '');

    params = this.appendToImmutableHttpParams(filter, params);

    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {
          return this.httpClient.get<Page<T>>(apiBase + this.getRequestMapping(), {params: params})
            .pipe(
              map((result) => {
                try {
                  const items: T[] = result.items.map((item) => {
                    const instance: T = this._buildInstance(item);
                    return instance;
                  });
                  return new Page(items, result.count, result.offset, result.page);
                } catch (ex) {
                  console.log(ex);
                  this.apiMessageService.sendMessage(MessageType.ERROR, ex.message);
                  observableThrowError(ex);
                }
              }),
              catchError( (error: HttpErrorResponse) => {
                const springError = new SpringError(error);
                this.apiMessageService.sendMessage(MessageType.ERROR,  springError.getRestErrorMessage());
                return observableThrowError(error.error);
              })
            );
        })
      );
  }

  /**
   * For Select end point.
   * @returns {Observable<T[]>}
   */
  public forSelect(filter?: {}): Observable<T[]> {
    return this.getAll(filter, '/select');
  }

  /**
   * Get entity by id.
   * @param {number} id
   * @returns {Observable<T>}
   */
  public getById(id: number): Observable<T> {

    if (!id) {
      this.apiMessageService.sendMessage(MessageType.ERROR, 'Id richiesta mancante');
      observableThrowError(null);
    }

    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {
          return this.httpClient.get<T>(apiBase + this.getRequestMapping() + '/' + id)
            .pipe(
              map((item) => {
                try {
                  const instance: T = this._buildInstance(item);
                  return instance;
                } catch (ex) {
                  console.log(ex);
                  this.apiMessageService.sendMessage(MessageType.ERROR, ex);
                  observableThrowError(ex);
                }
              }),
              catchError( (httpErrorResponse: HttpErrorResponse) => {
                const springError = new SpringError(httpErrorResponse);
                this.apiMessageService.sendMessage(MessageType.ERROR, springError.getRestErrorMessage());
                return observableThrowError(springError);
              })
            );
        })
      );

  }

  /**
   * Save entity.
   * @param entity
   * @returns {Observable<T>}
   */
  public create(entity: T): Observable<T> {

    // return observableThrowError(null);
    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {

          return this.httpClient.post<T>(apiBase + this.getRequestMapping() + '/create', Helpers.objToJsonObj(entity))
            .pipe(
              map(result => {
                this.apiMessageService.sendMessage(MessageType.SUCCESS, this.saveMessage());
                return this._buildInstance(result);
              }),
              catchError((httpErrorResponse: HttpErrorResponse) => {
                const springError = new SpringError(httpErrorResponse);
                this.apiMessageService.sendMessage(MessageType.ERROR, springError.getRestErrorMessage());
                return observableThrowError(springError);
              })
            );
        })
    );
  }

  /**
   * Save entity.
   * @param entity
   * @returns {Observable<T>}
   */
  public save(entity: T): Observable<T> {

    if (!entity.getId()) {
      return this.create(entity);
    }

    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {

          return this.httpClient.put<T>(apiBase + this.getRequestMapping() + this.getUpdate(), Helpers.objToJsonObj(entity))
            .pipe(
              map((result) => {
               this.apiMessageService.sendMessage(MessageType.SUCCESS, this.saveMessage());
               return this._buildInstance(result);
              }),
              catchError((httpErrorResponse: HttpErrorResponse) => {
               const springError = new SpringError(httpErrorResponse);
               this.apiMessageService.sendMessage(MessageType.ERROR, springError.getRestErrorMessage());
               return observableThrowError(springError);
              })
            );
        })
      );
  }

  protected getUpdate(): string {
    return '/update';
  }
  /**
   * Delete entity.
   * @param {number} id
   * @returns {any}
   */
  public delete(id: number) {

    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {

          return this.httpClient.delete<any>(apiBase + this.getRequestMapping() + '/delete/' + id)
            .pipe(
              map((deletedEntity) => {
                this.apiMessageService.sendMessage(MessageType.SUCCESS, this.deleteMessage());
                // return this._buildInstance(deletedEntity);
              }),
              catchError((httpErrorResponse: HttpErrorResponse) => {
                const springError = new SpringError(httpErrorResponse);
                this.apiMessageService.sendMessage(MessageType.ERROR, springError.getRestErrorMessage());
                return observableThrowError(springError);
              })
            );

        })
      );
  }

  /**
   * Get programmabile. Fornire il path relativo e i queryParams.
   * @param {string} relativePath
   * @param {HttpParams} params
   * @returns {Observable<any>}
   */
  public getEntity(relativePath: string, params: HttpParams): Observable<T> {

    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {

          return this.httpClient.get<T>(apiBase + this.getRequestMapping() + relativePath,{params: params})
            .pipe(
              map( (result) => {
                const instance: T = this._buildInstance(result);
                return instance;
              }),
              catchError((responseError: HttpErrorResponse) => {
                return this.generateError(responseError);
              })
            );
        })
      );
  }

  /**
   * Get programmabile. Fornire il path relativo e i queryParams.
   * @param {string} relativePath
   * @param {HttpParams} params
   * @returns {Observable<any>}
   */
  public getArray(relativePath: string, params: HttpParams, customRequestMapping?: string): Observable<T[]> {

    const requestMapping = customRequestMapping ? customRequestMapping :  this.getRequestMapping();
    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {

          return this.httpClient.get<T[]>(apiBase + requestMapping + relativePath,{params: params})
            .pipe(
              map( (result: T[]) => {
                const items: T[] = result.map((item) => {
                  const instance: T = this._buildInstance(item);
                  return instance;
                });
                return items;
              }),
              catchError((responseError: HttpErrorResponse) => {
                if (responseError.status === 204) {  // No Content
                  return observableOf(null);
                }
                const springError = new SpringError(responseError);
                this.apiMessageService.sendMessage(MessageType.ERROR, springError.getRestErrorMessage());
                return observableThrowError(responseError);
              })
            );
        })
      );
  }

  /**
   * Get programmabile. Fornire il path relativo e i queryParams.
   * @param {string} relativePath
   * @param {HttpParams} params
   * @returns {Observable<any>}
   */
  public getBoolean(relativePath: string, params: HttpParams): Observable<boolean> {

    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {

          return this.httpClient.get<boolean>(apiBase + this.getRequestMapping() + relativePath,{params: params})
            .pipe(
              map( (result) => {
                  return result;
              }),
              catchError((responseError: HttpErrorResponse) => {
                return observableThrowError(responseError);
              })
            );
        })
      );
  }

    /**
   * Get programmabile. Fornire il path relativo e i queryParams.
   * @param {string} relativePath
   * @param {HttpParams} params
   * @returns {Observable<any>}
   */
  public getAny(relativePath: string, params: HttpParams): Observable<any> {
    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {
          return this.httpClient.get<any>(apiBase + this.getRequestMapping() + relativePath,{params: params})
            .pipe(
              map( (result) => {
                  return result;
              }),
              catchError((httpErrorResponse: HttpErrorResponse) => {
                const springError = new SpringError(httpErrorResponse);
                this.apiMessageService.sendMessage(MessageType.ERROR, springError.getRestErrorMessage());
                return observableThrowError(springError);
              })
            );
        })
      );
  }

  public postEntity(relativePath: string, obj): Observable<T> {

    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {

          // debugger;

          return this.httpClient.post<T>(apiBase + this.getRequestMapping() + '/' + relativePath, Helpers.objToJsonObj(obj))
            .pipe(
              map((result) => {
                this.apiMessageService.sendMessage(MessageType.SUCCESS, this.saveMessage());
                return this._buildInstance(result);
              }),
              catchError((httpErrorResponse: HttpErrorResponse) => {
                const springError = new SpringError(httpErrorResponse);
                this.apiMessageService.sendMessage(MessageType.ERROR, springError.getRestErrorMessage());
                return observableThrowError(springError);
              })
            );
        })
      );
  }


  /**
   * Delete entity.
   * @param {number} id
   * @returns {any}
   */
  public deleteFile(entity: T, key: string) {

    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {

          const deletePath = '/' + entity.getId() + '/docs/delete';

          const queryParams = {'key': key};

          return this.httpClient.delete<any>(apiBase + this.getRequestMapping() + deletePath, {params: queryParams})
            .pipe(
              map((result) => {
                this.apiMessageService.sendMessage(MessageType.SUCCESS, 'Allegato eliminato correttamente');
              }),
              catchError((httpErrorResponse: HttpErrorResponse) => {
                const springError = new SpringError(httpErrorResponse);
                this.apiMessageService.sendMessage(MessageType.ERROR, springError.getRestErrorMessage());
                return ErrorObservable.create(springError);
              })
            );
        })
      );
  }

  public postFile(entity: T, fileToUpload: File, descr: string): Observable<any> {

    const endpoint = '/' + entity.getId() + '/docs/upload';

    const formData: FormData = new FormData();

    formData.append('file', fileToUpload, fileToUpload.name);
    formData.append('descr', descr?descr:'');

    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {

          return this.httpClient.post(apiBase + this.getRequestMapping() + endpoint, formData, { headers: {} })
            .pipe(
              map((response) => {
                this.apiMessageService.sendMessage(MessageType.SUCCESS, 'Allegato inserito con successo.');
                return true;
              }),
              catchError((httpErrorResponse: HttpErrorResponse) => {
                const springError = new SpringError(httpErrorResponse);
                this.apiMessageService.sendMessage(MessageType.ERROR, springError.getRestErrorMessage());
                return ErrorObservable.create(springError);
              })
            );
        })
      );
  }

  public getFiles(entity: T): Observable<Attachment[]> {

    const endpoint = '/' + entity.getId() + '/docs';

    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {
          return this.httpClient.get<Attachment[]>(apiBase + this.getRequestMapping() + endpoint)
            .pipe(
              map( (result) => {
                return result;
              }),
              catchError((responseError: HttpErrorResponse) => {
                return ErrorObservable.create(responseError);
              })
            );
        })
      );
  }

  public getBlob(entity: T, key: string, filename: string, download: boolean) {
    return this.configService.getApiBase().pipe(switchMap((apiBase) => {
      const endpoint = '/' + entity.getId() + '/docs/download?key=' + key;

      return this.httpClient.get(apiBase + this.getRequestMapping() + endpoint, {responseType: 'blob'}).pipe(map( (res) => {

        const url = window.URL.createObjectURL(res);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.setAttribute('style', 'display: none');
        a.href = url;
        if (download) {
          a.download = filename;
        } else {
          a.target = '_blank';
        }
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove(); // remove the element

        return res;
      }),catchError((responseError: HttpErrorResponse) => {

        this.apiMessageService.onApiMessage.error('Immpossibile scaricare il file allegato');

        return ErrorObservable.create(responseError);
      }),);
    })).subscribe( () => {});
  }
  
  /**
   * Aggiunge i campi presenti in obj in httpParams.
   *
   * N.B. Crea una nuova istanza di HttpParams. Il chiamante deve riassegnare l'istanza.
   * https://stackoverflow.com/questions/45210406/angular-4-3-httpclient-set-params
   *
   * Per le entity Base assegna come valore getId().
   * Per le entity Enum assegna come valore getEnumValue().
   *
   * @param {{}} obj
   * @param {HttpParams} httpParams
   * @returns {HttpParams}
   */
  public appendToImmutableHttpParams(obj: {}, httpParams: HttpParams) {
    if (obj === undefined) {
      return httpParams;
    }
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      if (value === null || value === undefined) {
        return;
      }
      if (value instanceof Base) {
        httpParams = httpParams.append(key, value.getId() + '');
        return;
      }
      if (value instanceof Enum) {
        httpParams = httpParams.append(key, value.getEnumValue());
        return;
      }
      if (value instanceof Date) {
        httpParams = httpParams.append(key, Helpers.formatDate(value));
        return;
      }
      httpParams = httpParams.append(key, value);
    });
    return httpParams;
  }

  /**
   * Check Get All.
   * @returns {Observable<boolean>}
   */
  public pCheck(): Observable<boolean> {
    return this.performPCheck(null);
  }

  /**
   * Check create.
   * @param {T} entity
   * @returns {Observable<boolean>}
   */
  public pCheckCreate(): Observable<boolean> {
    return this.performPCheck('/create');
  }

  /**
   * Check delete.
   * @param {T} entity
   * @returns {Observable<boolean>}
   */
  public pCheckDelete(): Observable<boolean> {
    return this.performPCheck('/delete');
  }

  /**
   * Check se utente collegato è SUPERUTENTE o DEVELOPER
   * Viene passato un path inesistente.... se true vuol dire
   * ce ci troviamo di fronte ad un utente di tipo 
   * SUPERUTENTE o DEVELOPER
   * @param {T} entity
   * @returns {Observable<boolean>}
   */
  public pCheckSuperUtente(): Observable<boolean> {
    return this.performPCheck('/pathnonesistente');
  }

  private performPCheck(path: string): Observable<boolean> {

    path = path ? path : '';

    const params = new HttpParams().set('path', this.getApiPath() + path);
    return this.configService.getApiBase()
      .pipe(
        switchMap((apiBase) => {

          return this.httpClient.get<boolean>(apiBase + ConfigService.ACE + '/v1/pcheck', {params: params})
            .pipe(
              map((result) => {
                return result;
              }),
              catchError((error) => {
                return observableOf(null);
              })
            );
        })
      );
  }

  public getCsv(filterFormValue: any, url? : string, async?: boolean) : Observable<any>{
    let path = url || '/csv';
    let params = this.appendToImmutableHttpParams(filterFormValue, new HttpParams());
    return this.configService.getApiBase()
    .pipe(
      switchMap((apiBase) => {
        return this.httpClient.get(apiBase + this.getRequestMapping() + path, {params: params, responseType: 'blob'})
          .pipe(
            map((result) => {
              return result;
            }),
            catchError( (error: HttpErrorResponse) => {
              const springError = new SpringError(error);
              this.apiMessageService.sendMessage(MessageType.ERROR,  springError.getRestErrorMessage());
              return observableThrowError(error.error);
            })
          );
      })
    );
  }

  public turnIdsIntoEntities(ids: number[], list: T[]): T[] {
    return list.filter(entity => ids.includes(entity.getId()));
  }

  public navigate(path: string[], route: ActivatedRoute) {
    this.router.navigate(path, {relativeTo: route, replaceUrl: true});
  }
}
